/**
 * Generic renderer for a declarative related-entity section
 * (see `../registry/relatedSections.ts`).
 *
 * Resolves the config to a single `useCrmObjectList` call
 * (`associated_with_entity_id` + optional filter/sort/limit), then renders each
 * result through the card registry inside a `CollapsibleSection`. This one
 * component backs every association-sourced section (upcoming meetings, meeting
 * notes, …) — the differences live entirely in the config it's given.
 *
 * Rows are truncated to the config's display limit; when more match, a
 * "View N more …" control expands the section (fetching up to the expanded
 * limit), and "Show less" collapses it again.
 */

import { useCallback, useMemo, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { logger } from '@/lib/logger';
import type { CrmObject } from '@/types/crm';

import { useOpenSlideout } from '../../hooks/useOpenSlideout';
import { useCrmObjectList } from '../../store/hooks';
import { useRecordDraft } from '../contexts/RecordDraftContext';
import { ENTITY_CARDS } from '../registry/entityCardRegistry';
import type { RelatedEntitySectionConfig } from '../registry/relatedSections';
import {
  buildSectionFilters,
  nowIsoBucketedToMinute,
  sectionDisplayLimit,
} from '../registry/relatedSections';
import { CollapsibleSection } from './collapsible-section';

interface RelatedEntitySectionProps {
  config: RelatedEntitySectionConfig;
  object: CrmObject;
}

export function RelatedEntitySection({
  config,
  object,
}: RelatedEntitySectionProps) {
  const openSlideout = useOpenSlideout();
  const orgId = object.org_id;
  const entityId = object.entity_id;

  // This section is a server list query keyed on `associated_with_entity_id`.
  // For a record that does not exist yet (a proposal preview, or a builder
  // draft) the server has never seen that id, so the query can only ever
  // resolve to an error block — "We couldn't load your upcoming meetings" over
  // a record the user is still deciding whether to create. Skip the fetch and
  // render nothing. Sections that read the record's OWN projected data (e.g.
  // PeopleSection, via `object.associations`) are unaffected and still render.
  const isUnsavedRecord = Boolean(useRecordDraft()?.isUnsaved);

  const [expanded, setExpanded] = useState(false);

  // Stable per mount: keeps the React Query key from churning every render
  // (the key JSON-stringifies filters, and `value: 'now'` lives inside them).
  const nowIso = useMemo(() => nowIsoBucketedToMinute(), []);
  const filters = useMemo(
    () => buildSectionFilters(config, entityId, nowIso, expanded),
    [config, entityId, nowIso, expanded]
  );
  const options = useMemo(
    () => ({
      enabled: !!orgId && !!entityId && !isUnsavedRecord,
      include_associations: config.includeAssociations,
    }),
    [orgId, entityId, config.includeAssociations, isUnsavedRecord]
  );

  const { data, isLoading, error, isPlaceholderData, refetch } =
    useCrmObjectList(orgId, config.entityType, filters, options);

  const handleOpen = useCallback(
    (id: string) => openSlideout(id),
    [openSlideout]
  );
  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);
  const handleExpand = useCallback(() => setExpanded(true), []);
  const handleCollapse = useCallback(() => setExpanded(false), []);

  // All hooks have run; safe to bail out.
  if (isUnsavedRecord) {
    return null;
  }

  const Card = ENTITY_CARDS[config.card];
  if (!Card) {
    logger.warn('RelatedEntitySection: unresolvable card key', {
      card: config.card,
      sectionTitle: config.title,
      entityId,
    });
    return null;
  }

  const results = data?.results ?? [];
  // Response-level hydrated associations (e.g. a note's meeting, a meeting's
  // company); the list endpoint returns these here, not on each row.
  const associatedObjects = data?.associated_objects;
  // Total matching rows (server COUNT(*) OVER()); falls back to the page size.
  const total = data?.count ?? results.length;
  const displayLimit = sectionDisplayLimit(config);
  // Rows not shown while collapsed (only meaningful when not expanded).
  const hiddenCount = Math.max(0, total - displayLimit);
  const isTruncatable = total > displayLimit;
  const itemNoun =
    hiddenCount === 1 && config.itemNounSingular
      ? config.itemNounSingular
      : config.itemNounPlural;

  if (isLoading) {
    if (config.hideWhenEmpty) {
      return null;
    }
    return (
      <CollapsibleSection title={config.title} defaultExpanded>
        <div className='py-2' aria-busy='true'>
          <div className='h-4 md:h-5 w-32 bg-[var(--color-bg-tertiary)] rounded animate-pulse' />
        </div>
      </CollapsibleSection>
    );
  }

  // Only surface the error state when there's nothing to show, or when the
  // shown rows are stale placeholder data from a different query key (e.g. an
  // expand/collapse fetch that failed) — a transient refetch failure of the
  // *same* query key with rows already cached keeps the existing results visible.
  if (error && (results.length === 0 || isPlaceholderData)) {
    return (
      <CollapsibleSection title={config.title} defaultExpanded>
        <p className='text-xs md:text-sm text-fg-tertiary py-2'>
          {config.errorMessage}
        </p>
        <Button color='link-gray' size='sm' onClick={handleRetry}>
          Retry
        </Button>
      </CollapsibleSection>
    );
  }

  if (results.length === 0) {
    if (config.hideWhenEmpty) {
      return null;
    }
    return (
      <CollapsibleSection title={config.title} defaultExpanded>
        <p className='text-xs md:text-sm text-[var(--color-text-tertiary)] py-2'>
          {config.emptyState}
        </p>
      </CollapsibleSection>
    );
  }

  return (
    <CollapsibleSection title={config.title} defaultExpanded>
      <div className='space-y-2'>
        {results.map(row => (
          <Card
            key={row.entity_id}
            object={row}
            onOpen={handleOpen}
            associatedObjects={associatedObjects}
          />
        ))}
      </div>
      {isTruncatable && (
        <div className='pt-2'>
          {expanded ? (
            <Button color='link-gray' size='sm' onClick={handleCollapse}>
              Show less
            </Button>
          ) : (
            <Button color='link-gray' size='sm' onClick={handleExpand}>
              View {hiddenCount} more {itemNoun}
            </Button>
          )}
        </div>
      )}
    </CollapsibleSection>
  );
}
